<script setup lang="ts">
// What Google collects directly, outside this form. Worded as what Google asks
// for, from its own help pages, never as legal advice about what's required.
const processUrl = 'https://support.google.com/localservices/answer/6226575'

// A client's own Google review link, when its definition has one.
defineProps<{ reviewUrl?: string | undefined }>()
</script>

<template>
  <section class="next-steps" aria-labelledby="next-steps-title">
    <h2 id="next-steps-title">What you'll still do in Google's setup</h2>
    <p>
      Google collects a few things straight from you, not through this form. Baker Web Solutions
      will tell you when each one comes up.
    </p>
    <ul>
      <li>
        <strong>Background checks.</strong> Each owner gets a request from Google's background-check
        partner and agrees to the check there. Anything personal it needs, it asks for itself.
      </li>
      <li>
        <strong>Identity check.</strong> If Google asks, the person representing the business sends
        a photo of a current government-issued ID to Google's partner, Evident.
      </li>
      <li>
        <strong>Business representative.</strong> Google may ask you to pick your name from Dun
        &amp; Bradstreet's list for the business, or to send a registration document that shows your
        full name.
      </li>
      <li>
        <strong>Insurance minimum.</strong> Google shows the minimum coverage for your category
        during setup. If your certificate is below it, you'll need an updated one from your
        insurance agent.
      </li>
      <li>
        <strong>Reviews.</strong> Google may require a minimum number of reviews for your category.
        <template v-if="reviewUrl?.startsWith('https://')">
          Send past customers your review link:
          <a :href="reviewUrl" target="_blank" rel="noopener noreferrer">{{ reviewUrl }}</a>
        </template>
      </li>
      <li>
        <strong>Answering calls.</strong> Once your ads run, Google ranks you partly on how reliably
        you answer lead calls, and missed calls can hurt. Keep ad hours to times someone can pick
        up.
      </li>
    </ul>
    <p>
      <a :href="processUrl" target="_blank" rel="noopener noreferrer"
        >Google's guide to screening and verification</a
      >
    </p>
  </section>
</template>

<style scoped>
.next-steps {
  border: 1px solid var(--line, #cad2c4);
  background: #fff;
  padding: 0.9rem 1rem;
  display: grid;
  gap: 0.5rem;
}
.next-steps h2 {
  color: var(--pine, #285341);
  font-size: 1.05rem;
  margin: 0;
}
.next-steps p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #3b4a3d;
}
.next-steps ul {
  margin: 0;
  padding-left: 1.2rem;
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  line-height: 1.45;
}
.next-steps a {
  color: var(--pine, #285341);
  font-weight: 650;
  /* The review link prints its URL; unbroken, it widens the whole page on a phone. */
  overflow-wrap: anywhere;
}
</style>
